import {
  ReportFormat,
  ReportFormatDisplayName,
} from "pmt-types/src/enums/ReportFormat.ts";
import { ReportTypeDisplayName } from "pmt-types/src/enums/ReportType.ts";
import { Report } from "pmt-types/src/models/Report.ts";
import { useState } from "react";
import { useMutationGenerateReportLink } from "../../hooks/mutations/useMutationGenerateReportLink.ts";
import cx from "../../utils/cx.ts";
import dateStringComparer from "../../utils/dateStringComparer.ts";
import formatDateString from "../../utils/formatDateString.ts";
import { Maybe } from "../../utils/types/maybe.ts";
import ErrorLabel from "../form/errorLabel.tsx";

type Props = {
  reports: Report[];
};

export default function AddedReportList({ reports }: Props) {
  const {
    mutate: generatePdfLink,
    isLoading: isPdfLinkLoading,
    error,
  } = useMutationGenerateReportLink();
  const [loadingReport, setLoadingReport] = useState<Maybe<Report>>(null);

  return (
    <div>
      <div className="w-full px-12 mt-16 flow-root">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                    >
                      Report
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Type
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Format
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                    >
                      Created
                    </th>
                    <th
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                    >
                      <span className="sr-only">View</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {reports
                    .sort((a, b) =>
                      dateStringComparer(a.updatedDate, b.updatedDate),
                    )
                    .map((report, index) => (
                      <tr key={index}>
                        <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                          {report.name}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {ReportTypeDisplayName[report.reportType]}
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {
                            ReportFormatDisplayName[
                              report.reportFormat ?? ReportFormat.enum.Pdf
                            ]
                          }
                        </td>
                        <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                          {formatDateString(report.createdDate)}
                        </td>
                        <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                          <div className="flex items-center justify-around mr-4">
                            <a
                              onClick={() => {
                                if (isPdfLinkLoading) {
                                  return false;
                                }

                                setLoadingReport(report);

                                generatePdfLink(report, {
                                  onSuccess: (data) => {
                                    window.open(
                                      data.url,
                                      "_blank",
                                      "noreferrer",
                                    );
                                  },
                                  onError: (error) =>
                                    console.error(
                                      "Error generating pdf link",
                                      error,
                                    ),
                                });
                              }}
                              className={cx(
                                isPdfLinkLoading &&
                                  loadingReport?.id == report.id
                                  ? "text-slate-600 hover:text-slate-900"
                                  : "text-sky-500 hover:text-sky-700",
                                "cursor-pointer",
                              )}
                            >
                              {report.reportFormat === ReportFormat.enum.Excel
                                ? "Download"
                                : "View"}
                              <span className="sr-only">, {index}</span>
                            </a>
                            {error != null && (
                              <ErrorLabel message={error?.toString()} />
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
